import React, { useState, ChangeEvent, useEffect, useContext } from 'react';
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { styles } from '@tag/tag-components-react-v2';
import { getFilteredDocs, getFilteredComponents, getStory } from '../../Docs/Docs';
import { Doc, Story } from '../../Docs/DocTypes';
import {
  getCssMainDiv,
  getCssLink,
  getCssForDivSearch,
  getCssForInputSearch,
  getCssforDivExpanded,
  getNavDefaultValues,
} from './NavUtils';
import { NavGroupItem } from './NavGroupItem';
import { NavGroup } from './NavGroup';
import { NavSection } from './NavSection';
import {
  getPath,
  getCodeVisible,
  getCodeTab,
  getCodeBackgroundVisible,
  getCodeMobileView,
} from '../Story/StoryPage.utils';
import { darkModeContext } from '../../darkModeContext';
import { DarkModeToggle } from './DarkModeToggle';

const Nav: React.FC<RouteComponentProps> = ({ history, location }) => {
  let { defaultSelectedGroup, defaultSelectedItem } = getNavDefaultValues(location);

  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(defaultSelectedGroup);
  const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);

  const docsDarkMode = useContext(darkModeContext);
  const docsDarkModeValue = docsDarkMode.value || false;

  useEffect(() => {
    let story: Story | null = getStory(selectedGroup, selectedItem);
    story && changeRoute(story);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, selectedItem]);

  const handleGroupClick = (doc: Doc) => {
    if (selectedGroup === doc.name) {
      setSelectedGroup('');
      setSelectedItem('');
    } else {
      selectGroup(doc);
    }
  };

  const selectGroup = (doc: Doc) => {
    setSelectedGroup(doc.name);
    if (doc.type === 'page') {
      history.push(`./${doc.route}`);
    }
    if (doc.type === 'component') {
      selectItem(doc.stories[0]);
    }
  };

  const changeRoute = (story: Story) => {
    history.push(
      getPath(
        selectedGroup,
        story.title,
        getCodeVisible(location),
        getCodeTab(location, story),
        getCodeBackgroundVisible(location),
        getCodeMobileView(location),
      ),
    );
  };

  const selectItem = (story: Story) => {
    setSelectedItem(story.title);
    changeRoute(story);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  };

  const handleSectionClick = (doc: Doc) => {
    if ('route' in doc) {
      history.push(doc.route);
    }
  };

  return (
    <div css={getCssMainDiv(docsDarkModeValue)}>
      <Link to="/" css={getCssLink()}>
        tag-docs
      </Link>
      <DarkModeToggle />
      <div css={getCssForDivSearch()}>
        <input
          value={search}
          placeholder="Search..."
          css={getCssForInputSearch(styles.font.family, docsDarkModeValue)}
          onChange={handleSearch}
        />
      </div>

      <div css={getCssforDivExpanded()}>
        {getFilteredDocs(search).map(doc => (
          <React.Fragment key={doc.name}>
            {doc.type === 'section' && (
              <React.Fragment key={doc.name}>
                <NavSection doc={doc} type={doc.type} docsDarkModeValue={docsDarkModeValue} />
                {getFilteredComponents(doc, search).map(group => (
                  <React.Fragment key={group.name}>
                    <NavGroup
                      doc={group}
                      onClick={handleGroupClick}
                      selected={group.name === selectedGroup}
                      docsDarkModeValue={docsDarkModeValue}
                    />
                    {group.type === 'component' &&
                      group.name === selectedGroup &&
                      group.stories.map(story => (
                        <NavGroupItem
                          key={story.title}
                          story={story}
                          selected={story.title === selectedItem}
                          onClick={selectItem}
                          docsDarkModeValue={docsDarkModeValue}
                        />
                      ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            )}
            {doc.type === 'page' && (
              <NavSection
                doc={doc}
                type={doc.type}
                onClick={handleSectionClick}
                docsDarkModeValue={docsDarkModeValue}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default withRouter(Nav);
